import { useEffect, useRef } from 'react';
import { useInboxDrawerStore } from '../../stores/inbox-drawer-store';
import { OrderDetailView } from '../order-detail/OrderDetailView';
import { OfferteDetailView } from '../offerte-detail/OfferteDetailView';
import { KlantDetailView } from '../klanten/KlantDetailView';

export function InboxDrawerPanel() {
  const openItem = useInboxDrawerStore((s) => s.openItem);
  const close = useInboxDrawerStore((s) => s.close);
  const containerRef = useRef<HTMLDivElement>(null);

  // Esc-to-close. Capture-phase so it runs before the page-level Esc handler.
  useEffect(() => {
    if (!openItem) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [openItem, close]);

  // Scroll the drawer back to top whenever the open item changes.
  useEffect(() => {
    if (!openItem) return;
    const scroller = containerRef.current?.querySelector('[data-drawer-scroll]') as HTMLElement | null;
    if (scroller) scroller.scrollTop = 0;
  }, [openItem?.type, openItem?.id]);

  if (!openItem) return null;

  return (
    <div
      ref={containerRef}
      className="flex-1 min-w-0 flex flex-col bg-white border-l border-slate-200 overflow-hidden"
      role="complementary"
      aria-label={openItem.type === 'order' ? 'Order detail' : openItem.type === 'lead' ? 'Offerte detail' : 'Klant detail'}
    >
      {openItem.type === 'order' && (
        <OrderDetailView
          key={`order-${openItem.id}`}
          orderId={openItem.id}
          variant="drawer"
          onClose={close}
        />
      )}
      {openItem.type === 'klant' && (
        <KlantDetailView
          key={`klant-${openItem.id}`}
          customerId={openItem.id}
          type={openItem.klantType || 'account'}
          variant="drawer"
          onClose={close}
        />
      )}
      {openItem.type === 'lead' && (
        <OfferteDetailView
          key={`lead-${openItem.id}`}
          offerteId={openItem.id}
          variant="drawer"
          onClose={close}
        />
      )}
    </div>
  );
}
